import { getSlug, isV2Page, isOlp } from 'global-scripts/utils/helpers';
import { appendScript } from '../utils/helpers';
import { deepGet } from 'bam-deep-get';

export const AdobeAnalytics = {
    /**
     * @description Determine the page type (OLP, MLP, Splash)
     * @return Welcome = OLP; Welcome - MLP = MLP, Marketing Splash = Splash
     */
    getPageName: () => {
        // Only V2 Pages have pageType on them, use isOlp for V1 logic
        if (isV2Page() && !isOlp()) {
            const pageType = document.querySelector('meta[name~="pageType"]').getAttribute('content').toString();
            return ((pageType === 'splash') ? 'Marketing Splash' : 'Welcome - MLP');
        } else {
            // TODO: When V1 OLP is migrated to V2, replace isOlp() function with pageType
            return (isOlp() ? 'Welcome' : 'Welcome - MLP');
        }
    },

    /**
     * @description Determine the section for adobe analytics (OLP, MLP, Splash)
     * @return Welcome = OLP; Welcome - MLP = MLP, Marketing Splash = Splash
     */
    getSection: () => {
        if (isV2Page() && !isOlp()) {
            const pageType = document.querySelector('meta[name~="pageType"]').getAttribute('content').toString();
            return ((pageType === 'splash') ? 'Marketing Splash' : 'Welcome');
        } else {
            // TODO: When V1 OLP is migrated to V2, replace isOlp() function with pageType
            return 'Welcome';
        }

    },

    /**
     * @description Fire optimizely analytics call
     * @param {String} integrationAction The action to track
     * @param {Object} sVariable The date to be tracked
     */
    optimizelyIntegrationCall: (integrationAction, sVariable) => {
        // If the Optimizely custom integration is hooked up use it
        const optimizelyIntegration = window.optimizely &&
            typeof window.optimizely.get === 'function' &&
            window.optimizely.get('custom/adobeIntegrator');

        if (optimizelyIntegration && optimizelyIntegration[integrationAction]) {
            optimizelyIntegration[integrationAction](sVariable);
        }
    },

    /**
     * @description Generate the Adobe Analytics tracking data and fire tracking call
     * @param {Object} event The event to be tracked
     */
    track: (event) => {
        if (!event) {
            return;
        }

        const { s, AnalyticsManager } = window;
        const { target, type } = event;

        if (!s) return;
        const slug = getSlug();

        const trackData = {
            action: null,
            ctaButtonURL: null,
            ctaButtonCopy: null,
            moduleLocation: null,
            pagename: AdobeAnalytics.getPageName(),
            section: AdobeAnalytics.getSection(),
            urlSlug: slug
        };

        const contextData = {
            ...AnalyticsManager.contextData,
            ...trackData
        };

        // s Top Level Items
        s.visitorID = 'D=s_vi';
        s.pageName = contextData.pagename;
        s.contextData = contextData;

        delete s.contextData.pagename;

        if (type.includes('click')) {
            const analyticsName = target.getAttribute('data-analytics-name') || '';
            const analyticsSection = target.closest('[data-analytics-section]');
            const analyticsSectionName = analyticsSection ? analyticsSection.dataset.analyticsSection : '';
            let moduleType = analyticsSectionName.includes(':') ? analyticsSectionName.split(':')[0].trim() : '';

            if (target.hasAttribute('data-analytics-module-type')) {
                moduleType = target.getAttribute('data-analytics-module-type');
            }

            moduleType = `${moduleType.charAt(0).toUpperCase() + moduleType.substr(1)} Click`;

            s.contextData.action = `${contextData.webAppName} ${trackData.pagename} : ${moduleType}`;
            s.contextData.ctaButtonCopy = analyticsName;
            s.contextData.ctaButtonURL = target.href || '';
            s.contextData.moduleLocation = analyticsSectionName;

            // If the Optimizely custom integration is hooked up use it
            AdobeAnalytics.optimizelyIntegrationCall('trackDelayedCampaigns', s);

            s.tl(null, 'lnk_o', s.contextData.action);
        } else if (type.includes('event')) {
            s.contextData = {
                ...s.contextData,
                ...event.detail,
                action: `${contextData.webAppName} ${trackData.pagename} : ${event.detail.action}`
            };

            // If the Optimizely custom integration is hooked up use it
            AdobeAnalytics.optimizelyIntegrationCall('trackDelayedCampaigns', s);

            s.tl(null, 'lnk_o', s.contextData.action);

        } else {

            // If the Optimizely custom integration is hooked up use it
            AdobeAnalytics.optimizelyIntegrationCall('assignCampaigns', s);

            s.t();
        }
    },

    /**
     * @description Scrape the site for any link for specific cross domain sites.  Once found append the adobe
     * analytics specific IDs to the url. The linked site will need to be able to handle the inclusion of these IDs
     */
    appendIdsForCrossDomainLinks: () => {
        if (window.visitor) {
            const { crossDomains = [] } = window.server_path.analytics.adobe;
            const selectors = crossDomains.map(domain => `[href*='${domain}']`);
            const links = document.querySelectorAll(selectors.join(','));

            links.forEach(link => {
                link.href = window.visitor.appendVisitorIDsTo(link.href);
            });
        }
    },

    /**
     * @description Loads analytics_script_bundle if OneTrust was not enabled
     */
    loadScript: (callback = () => {}) => {
        const staticRoot = deepGet(window, 'dssMlpConfigs.staticRoot');

        if (staticRoot) {
            appendScript(`${staticRoot}/scripts/analytics_script_bundle.js`, () => {
                AdobeAnalytics.track(new Event('load'));
                callback();
            });
        }
    }
};

/**
 * Currently Adobe Analytics only needs to listen for the click and load events
 */
export function initAdobeAnalytics() {
    document.addEventListener('analytics_click', AdobeAnalytics.track);
    document.addEventListener('analytics_load', AdobeAnalytics.track);
    document.addEventListener('analytics_event', AdobeAnalytics.track);
    AdobeAnalytics.appendIdsForCrossDomainLinks();
}
